"use strict";
/**
 * CONTESTO DI GIOCO & SCALA MALLO — costanti di dominio (@leopart/calc).
 *
 * Fonte UNICA per backend, frontend web e mobile: range di densità,
 * riferimento gara, livelli della scala Mallo, fattori di flusso e limiti di
 * validazione dei campi del blocco. Cambiare una soglia = toccare questo file.
 *
 * Spec: vault/Roadmap/Spec contesto di gioco e Mallo-IDC.md (ADR 029).
 * Prototipo di riferimento: docs/prototypes/allenamenti-mallo-densita.html.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.TB_EFFECTIVE_MAX = exports.TB_EFFECTIVE_MIN = exports.TB_PLAYERS_MAX = exports.TB_PLAYERS_MIN = exports.TB_FIELD_LENGTH_MAX = exports.TB_FIELD_LENGTH_MIN = exports.TB_FIELD_WIDTH_MAX = exports.TB_FIELD_WIDTH_MIN = exports.PLAYER_GROUPS = exports.CI_ALERT_SOTTO = exports.SOGLIA_MUSCOLARE = exports.SOGLIA_COGNITIVA = exports.FLOW_FACTORS = exports.FLOWS = exports.FLOW_TYPES = exports.MALLO_LEVELS = exports.MALLO_WEIGHTS = exports.DENSITY_RANGES = exports.MATCH_REF_DENSITY = exports.FIELD_GROUPS = exports.GRUPPO_MALLO = exports.GRUPPO_CONTESTO_GIOCO = void 0;
exports.densitaDi = densitaDi;
exports.classificaDensita = classificaDensita;
exports.classificaGruppo = classificaGruppo;
// ─── Gruppi di campi (tassonomia: trainingCategories.fieldsEnabled) ─────────
exports.GRUPPO_CONTESTO_GIOCO = 'contesto-gioco';
exports.GRUPPO_MALLO = 'mallo';
/** Gli slug ammessi in `trainingCategories.fieldsEnabled`. */
exports.FIELD_GROUPS = [exports.GRUPPO_CONTESTO_GIOCO, exports.GRUPPO_MALLO];
// ─── Densità di gioco (m²/giocatore) ─────────────────────────────────────────
/** Riferimento gara 11c11: ~105×68 m / 22 giocatori ≈ 320 m²/giocatore. */
exports.MATCH_REF_DENSITY = 320;
exports.DENSITY_RANGES = [
    {
        id: 'stretto',
        labelIt: 'Stretto',
        min: 0,
        max: 100,
        color: '#C0203A',
        notaIt: 'alta densità di accel/decel, frenate e duelli — carico eccentrico quadricipiti/hamstring',
    },
    {
        id: 'medio',
        labelIt: 'Medio',
        min: 100,
        max: 200,
        color: '#E8862E',
        notaIt: 'transizioni e cambi di direzione',
    },
    {
        id: 'ampio',
        labelIt: 'Ampio',
        min: 200,
        max: 300,
        color: '#2E9E5B',
        notaIt: 'corsa ad alta velocità — carico hamstring in sprint',
    },
    {
        id: 'match',
        labelIt: 'Match-like',
        min: 300,
        max: null,
        color: '#4A5FC1',
        notaIt: `richieste vicine alla gara (rif. ~${exports.MATCH_REF_DENSITY} m²/g)`,
    },
];
/**
 * Densità = (larghezza × lunghezza) / giocatori, in m²/giocatore.
 * `null` se uno qualunque dei tre input manca o non è positivo: la densità
 * non si persiste mai, si deriva sempre da qui (form live e analisi).
 */
function densitaDi(w, l, p) {
    if (!(Number(w) > 0) || !(Number(l) > 0) || !(Number(p) > 0))
        return null;
    return (Number(w) * Number(l)) / Number(p);
}
/** Il range che contiene `d` (d ≥ 0; l'ultimo range è aperto a destra). */
function classificaDensita(d) {
    for (const r of exports.DENSITY_RANGES) {
        if (d >= r.min && (r.max == null || d < r.max))
            return r;
    }
    // d < 0 non arriva mai dai calcoli (densitaDi ritorna solo positivi):
    // fallback difensivo sul primo range.
    return exports.DENSITY_RANGES[0];
}
// ─── Scala Mallo & flusso ────────────────────────────────────────────────────
exports.MALLO_WEIGHTS = [30, 40, 60, 80, 100];
exports.MALLO_LEVELS = [
    { v: 30, labelIt: '30% — Tecnico senza opposizione', color: '#9AA5B1' },
    { v: 40, labelIt: '40% — Opposizione ridotta', color: '#3FA9DC' },
    { v: 60, labelIt: '60% — SSG in superiorità', color: '#D4A017' },
    { v: 80, labelIt: '80% — SSG in parità / tema', color: '#E8862E' },
    { v: 100, labelIt: '100% — Partita libera / gara', color: '#C0203A' },
];
exports.FLOW_TYPES = ['continuo', 'intermittente', 'analitico'];
exports.FLOWS = [
    {
        id: 'continuo',
        labelIt: 'Continuo',
        fattore: 1.0,
        color: '#C0203A',
        notaIt: 'FC sostenuta, densità decisionale continua',
    },
    {
        id: 'intermittente',
        labelIt: 'Intermittente',
        fattore: 0.65,
        color: '#E8862E',
        notaIt: 'fasi di gioco alternate a organizzazione',
    },
    {
        id: 'analitico',
        labelIt: 'Analitico',
        fattore: 0.45,
        color: '#9DC9E8',
        notaIt: 'FC bassa, recupero elevato tra esecuzioni',
    },
];
exports.FLOW_FACTORS = {
    continuo: 1.0,
    intermittente: 0.65,
    analitico: 0.45,
};
/** Minuti con peso ≥ 60: opposizione completa, densità decisionale. */
exports.SOGLIA_COGNITIVA = 60;
/** Minuti con peso ≥ 80: sprint, frenate e duelli con richieste da gara. */
exports.SOGLIA_MUSCOLARE = 80;
/** Sotto questo rapporto Continuo/Intermittente scatta l'alert visivo. */
exports.CI_ALERT_SOTTO = 1.0;
/**
 * Soglie decise dall'utente (2026-09-16) sul TOTALE giocatori in campo
 * (`playersOnField`): Individuale 1 · Piccoli 2-4 · Medi 5-8 · Grandi 9+.
 * Stessa palette di `MALLO_LEVELS` (grigio→blu→oro→rosso) per coerenza visiva
 * fra le due scale crescenti del modulo.
 */
exports.PLAYER_GROUPS = [
    { id: 'individuale', labelIt: 'Individuale', min: 1, max: 1, color: '#9AA5B1' },
    { id: 'piccoli', labelIt: 'Piccoli gruppi', min: 2, max: 4, color: '#3FA9DC' },
    { id: 'medi', labelIt: 'Medi gruppi', min: 5, max: 8, color: '#D4A017' },
    { id: 'grandi', labelIt: 'Grandi gruppi', min: 9, max: Infinity, color: '#C0203A' },
];
/**
 * Il gruppo dimensionale che contiene `players`. `null` se `players` manca,
 * non è un numero o non è positivo — mai un fallback silenzioso su un gruppo
 * a caso.
 *
 * I valori non interi (es. 4.5, possibili da dati legacy/admin-raw non
 * vincolati da `@IsInt`) vengono arrotondati prima della classificazione:
 * senza questo arrotondamento il blocco finirebbe escluso qui ma incluso in
 * `densitaDiGioco` (che usa il valore grezzo nella divisione), facendo
 * disaccordare i due pannelli sullo stesso blocco.
 */
function classificaGruppo(players) {
    var _a;
    if (!(Number(players) > 0))
        return null;
    const p = Math.round(Number(players));
    const gruppo = exports.PLAYER_GROUPS.find((g) => p >= g.min && p <= g.max);
    return (_a = gruppo === null || gruppo === void 0 ? void 0 : gruppo.id) !== null && _a !== void 0 ? _a : null;
}
// ─── Limiti di validazione dei campi del blocco ─────────────────────────────
exports.TB_FIELD_WIDTH_MIN = 1;
exports.TB_FIELD_WIDTH_MAX = 120;
exports.TB_FIELD_LENGTH_MIN = 1;
exports.TB_FIELD_LENGTH_MAX = 130;
/** Individuale = 1 giocatore (decisione utente, F2 densità per numero di giocatori). */
exports.TB_PLAYERS_MIN = 1;
exports.TB_PLAYERS_MAX = 30;
exports.TB_EFFECTIVE_MIN = 1;
exports.TB_EFFECTIVE_MAX = 180;
